import { Lead } from '@/types/meta-lead.ts';

interface LeadTableProps {
    leads: Lead[];
    onView: (lead: Lead) => void;
    selectedIds: Set<number>;
    onToggleSelect: (leadId: number) => void;
}

const AVATAR_COLORS = [
    'bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-orange-500',
    'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-indigo-500',
];

function avatarColor(name: string) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatDate(iso?: string) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const yy = String(d.getFullYear()).slice(2);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yy}, ${hh}:${min}`;
    } catch { return '—'; }
}

export default function LeadTable({ leads, onView, selectedIds, onToggleSelect }: LeadTableProps) {
    const allSelected = leads.length > 0 && leads.every(l => selectedIds.has(l.id));

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/50">
                        <th className="w-10 px-4 py-3">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={e => leads.forEach(l => {
                                    const sel = selectedIds.has(l.id);
                                    if (e.target.checked && !sel) onToggleSelect(l.id);
                                    if (!e.target.checked && sel) onToggleSelect(l.id);
                                })}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 cursor-pointer"
                            />
                        </th>
                        {['LEAD', 'PÁGINA', 'PAÍS', 'FUNIL', 'NODE', 'CLIQUES', 'STATUS', 'ÚLTIMA INTERAÇÃO'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {leads.map(lead => {
                        const name = `${lead.nome} ${lead.sobrenome || ''}`.trim();
                        const initial = name.charAt(0).toUpperCase();
                        const isSelected = selectedIds.has(lead.id);

                        return (
                            <tr
                                key={lead.id}
                                onClick={() => onView(lead)}
                                className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/40' : 'hover:bg-gray-50/60'}`}
                            >
                                {/* Checkbox */}
                                <td className="px-4 py-3" onClick={e => { e.stopPropagation(); onToggleSelect(lead.id); }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}}
                                        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 cursor-pointer"
                                    />
                                </td>

                                {/* Lead */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2.5">
                                        <div className={`w-8 h-8 rounded-full ${avatarColor(name)} flex items-center justify-center text-xs font-bold text-white shrink-0`}>
                                            {initial}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 leading-none">{name}</p>
                                            <p className="text-[11px] text-gray-400 mt-0.5">ID #{lead.id}</p>
                                        </div>
                                    </div>
                                </td>

                                {/* Página */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <p className="text-gray-700 font-medium leading-none">{lead.page_name || '—'}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5 font-mono">{lead.page_id?.slice(0, 16)}</p>
                                </td>

                                {/* País */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="text-gray-600 flex items-center gap-1 text-xs">
                                        <span className="text-gray-300">⊕</span>
                                        {lead.pais || '—'}
                                    </span>
                                </td>

                                {/* Funil */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    {lead.current_funnel_id
                                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-semibold">#{lead.current_funnel_id}</span>
                                        : <span className="text-gray-300 text-xs">—</span>
                                    }
                                </td>

                                {/* Node */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    {lead.last_node_id
                                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs font-semibold">#{lead.last_node_id}</span>
                                        : <span className="text-gray-300 text-xs">—</span>
                                    }
                                </td>

                                {/* Cliques */}
                                <td className="px-4 py-3 whitespace-nowrap text-gray-700 font-medium text-sm">
                                    {lead.total_clicks ?? 0}
                                </td>

                                {/* Status */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-1 flex-wrap">
                                        {lead.is_active
                                            ? <span className="px-1.5 py-0.5 rounded border border-emerald-300 text-emerald-700 text-[11px] font-medium">ativo</span>
                                            : <span className="px-1.5 py-0.5 rounded border border-red-300 text-red-600 text-[11px] font-medium">inativo</span>
                                        }
                                        {lead.in_24h_window && (
                                            <span className="px-1.5 py-0.5 rounded border border-blue-300 text-blue-600 text-[11px] font-medium">24h</span>
                                        )}
                                        {lead.is_new_today && (
                                            <span className="px-1.5 py-0.5 rounded border border-yellow-300 text-yellow-700 text-[11px] font-medium">novo hoje</span>
                                        )}
                                        {lead.interacted_today && (
                                            <span className="px-1.5 py-0.5 rounded border border-orange-300 text-orange-600 text-[11px] font-medium">interagiu hoje</span>
                                        )}
                                    </div>
                                </td>

                                {/* Última interação */}
                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                    {formatDate(lead.last_interaction_at)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
